import React from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import Link from '|/components/carvana-ds/link';
import { SLATE_HOME_URL } from '|/context';
import { currency } from '|/helpers';
import { ACCESSORIES_SHIPPING_COST } from '|/services/slate-accessories';
import type { Accessory } from '|/services/slate-accessories';
import styles from './slate-accessories-modal.module.css';

export interface SlateAccessoriesModalVehicle {
  year: number;
  model: string;
  imageUrl?: string;
}

interface SlateAccessoriesModalProps {
  accessories: Accessory[];
  totalValue: number;
  vehicle: SlateAccessoriesModalVehicle | null;
  isOpen: boolean;
  onClose: () => void;
}

const groupAccessoriesByCategory = (accessories: Accessory[]): [string, Accessory[]][] => {
  const groups = new Map<string, Accessory[]>();
  accessories.forEach(accessory => {
    const existing = groups.get(accessory.category);
    if (existing) {
      existing.push(accessory);
    } else {
      groups.set(accessory.category, [accessory]);
    }
  });
  return Array.from(groups.entries());
};

const SlateAccessoriesModal: React.FC<SlateAccessoriesModalProps> = ({
  accessories,
  totalValue,
  vehicle,
  isOpen,
  onClose
}) => {
  const groupedAccessories = groupAccessoriesByCategory(accessories);

  return (
    <EventedModal
      analyticsId={ModalNames.SlateAccessories}
      isOpen={isOpen}
      onClose={onClose}
      mobileStyle="full"
      desktopWidth="480px"
      divLine={false}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.heading}>SELECTED ACCESSORIES</h2>
          <p className={styles.subtitle}>
            These accessories are included in your vehicle configuration and may affect your
            financing terms. Review your selections below or reconfigure your Slate to make changes.
          </p>
        </div>

        <div className={styles.vehicleCard}>
          {vehicle?.imageUrl && (
            <img className={styles.vehicleThumbnail} src={vehicle.imageUrl} alt="" loading="lazy" />
          )}
          <div className={styles.vehicleMeta}>
            <h4 className={styles.vehicleTitle}>
              {vehicle ? `${vehicle.year} ${vehicle.model}` : ''}
            </h4>
            <Link
              href={SLATE_HOME_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-analytics-id="pp_bmd_reconfigure_slate"
              variant="standalone"
              size="sm"
            >
              Reconfigure Your Slate
            </Link>
          </div>
        </div>

        {groupedAccessories.map(([category, items]) => (
          <div key={category} className={styles.categoryGroup}>
            <h4 className={styles.categoryHeader}>{category}</h4>
            {items.map(accessory => (
              <div key={accessory.id} className={styles.row}>
                <img
                  className={styles.rowThumbnail}
                  src={accessory.imageUrl}
                  alt=""
                  loading="lazy"
                />
                <div className={styles.rowMeta}>
                  <span className={styles.rowName}>{accessory.name}</span>
                </div>
                <div className={styles.rowPriceColumn}>
                  <span className={styles.rowPrice}>{currency(accessory.price)}</span>
                  {accessory.installation.factoryInstall && (
                    <span className={styles.rowInstallation}>
                      +{currency(accessory.installation.factoryInstall.price)} installation
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        <div className={styles.shippingRow}>
          <span className={styles.shippingLabel}>Accessories shipping cost</span>
          <span className={styles.shippingValue}>{currency(ACCESSORIES_SHIPPING_COST)}</span>
        </div>

        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Total accessories cost</span>
          <span className={styles.totalValue}>{currency(totalValue)}</span>
        </div>
      </div>
    </EventedModal>
  );
};

export default SlateAccessoriesModal;
